import { useState } from 'react';

import { ChartTooltip, useChartHover } from './ChartTooltip.jsx';
import { SERIES, TRACK, soft, SOFT_FLOOR } from './chartColors.js';

// Calendar-style activity heatmap. data: [{date, count}] oldest-first, laid out
// in columns of 7 (one column per week). The intensity ramp is theme-safe —
// soft() blends the accent var, so it re-tints with the theme and needs NO
// per-opacity Tailwind retarget (which the old bg-brand-600/20//40 grid relied
// on). Hover (mouse) shows the date + count; cells are not in the tab order
// (84 stops would swamp keyboard nav — the streak/trend numbers above carry the
// same signal for keyboard users; a keyboard summary is a Phase-4 a11y item).

// soft() percent by intensity bucket (index = clamped count). T258(4) raised
// bucket 1 off 30% (1.5:1 against the empty-cell track on three themes) to the
// floor. Deliberately NOT outlined like the funnel: an 11px cell with a 1px
// stroke is mostly stroke, and unlike the funnel these cells carry no unique
// information — the streak and weekly numbers directly above say the same thing
// in text, which is what a reader who cannot separate the buckets uses.
const RAMP = [0, SOFT_FLOOR, 72, 100];
export function heatFill(count) {
  if (!(count > 0)) return TRACK;
  const pct = RAMP[Math.min(count, RAMP.length - 1)];
  return pct >= 100 ? SERIES.accent : soft(SERIES.accent, pct);
}

// T335 — a keyboard path in, without 84 tab stops.
//
// The comment above ruled out making every cell focusable and was right: 84
// stops between the trend card and whatever follows it is worse than no access
// at all. What it named as the remaining work — "a keyboard summary is a
// Phase-4 a11y item" — is this.
//
// The grid becomes ONE tab stop with a roving cursor: arrows move day by day
// and week by week, Home and End jump to the ends, and the focused day is
// announced through a live region. That is the standard grid pattern, and it
// matters here specifically because the operator is himself a keyboard user —
// docs/EXPERIENCE-ROADMAP.md records exactly that, beside an upload flow that
// was impossible to reach from the keyboard.
export default function HeatmapChart({ data = [], cell = 11, gap = 3, ariaLabel = 'Daily activity heatmap' }) {
  const { hover, show, hide } = useChartHover();
  const [cursor, setCursor] = useState(null);
  const weeks = [];
  for (let i = 0; i < data.length; i += 7) weeks.push(data.slice(i, i + 7));
  // T353 — one pixel of margin on the top and left, because the keyboard cursor
  // ring is drawn at -1 to sit outside its cell. Without it the SVG viewport
  // clips the ring's left and top strokes for the whole first column and the
  // whole first row — the cursor was invisible exactly where Home puts it.
  const RING = 1;
  const width = Math.max(1, weeks.length * (cell + gap)) + RING;
  const height = 7 * (cell + gap) + RING;

  const at = cursor == null ? null : data[cursor] ?? null;
  const move = (delta) => setCursor((c) => {
    const from = c == null ? data.length - 1 : c;
    return Math.max(0, Math.min(data.length - 1, from + delta));
  });
  const onKeyDown = (e) => {
    if (!data.length) return;
    // A week is a column, so left/right step seven days and up/down step one.
    const step = { ArrowRight: 7, ArrowLeft: -7, ArrowDown: 1, ArrowUp: -1 }[e.key];
    if (step !== undefined) { e.preventDefault(); move(step); return; }
    if (e.key === 'Home') { e.preventDefault(); setCursor(0); return; }
    if (e.key === 'End') { e.preventDefault(); setCursor(data.length - 1); }
  };

  return (
    <div className="relative overflow-x-auto">
      <svg
        width={width}
        height={height}
        role="img"
        aria-label={ariaLabel}
        style={{ display: 'block' }}
        tabIndex={0}
        onKeyDown={onKeyDown}
        onBlur={() => setCursor(null)}
        // No focus class: index.css gives every :focus-visible the app-wide ring,
        // which is theme-bound. A local Tailwind ring colour would render one
        // palette on every theme.
        className="rounded"
      >
        {weeks.map((week, wi) =>
          week.map((d, di) => {
            const x = wi * (cell + gap) + RING;
            const y = di * (cell + gap) + RING;
            const content = `${d.date}: ${d.count} application${d.count === 1 ? '' : 's'}`;
            return (
              <rect
                key={d.date}
                x={x}
                y={y}
                width={cell}
                height={cell}
                rx={2}
                fill={heatFill(d.count)}
                style={{ cursor: 'default' }}
                onMouseEnter={() => show(x + cell / 2, y, content)}
                onMouseLeave={hide}
              >
                <title>{content}</title>
              </rect>
            );
          }),
        )}
        {/* The cursor ring, drawn last so it sits over the cells. */}
        {at && (() => {
          const wi = Math.floor(cursor / 7);
          const di = cursor % 7;
          return (
            <rect
              x={wi * (cell + gap)}
              y={di * (cell + gap)}
              width={cell + 2}
              height={cell + 2}
              rx={3}
              fill="none"
              stroke={SERIES.accent}
              strokeWidth={1.5}
              pointerEvents="none"
            />
          );
        })()}
      </svg>
      {/* Spoken, not merely drawn: a ring nobody can see is not a keyboard
          path. Polite so it does not interrupt a reader mid-sentence. */}
      <span role="status" aria-live="polite" className="sr-only">
        {at ? `${at.date}: ${at.count} application${at.count === 1 ? '' : 's'}` : ''}
      </span>
      <ChartTooltip hover={hover} width={width} />
    </div>
  );
}
